import React, { useEffect, useState } from 'react';
import { Download, RefreshCw, Lock, FileText, AlertTriangle, ExternalLink } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import {
  apiClient,
  getTask,
  getWhitepaper,
  regenerateWhitepaper,
  previewWhitepaper,
  downloadWhitepaperAsSeller,
  WhitepaperInfo,
} from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array
 *  (same helper as ProductsList.tsx / ProductEditor.tsx). */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

const formatFileSize = (bytes?: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  rendering: 'Rendering…',
  ready: 'Ready',
  failed: 'Failed',
};

interface WhitepaperPanelProps {
  productId: string;
  initialGated: boolean;
  /** 'draft' | 'active' | 'archived'. Download PDF uses the seller-
   *  authenticated GET /{product_id}/whitepaper/download route, which
   *  works regardless of gating/publish state (ownership-scoped, unlike
   *  the public /shop/whitepaper/{id}/download route used by visitors). */
  productStatus: string;
  onGatedChange?: (gated: boolean) => void;
}

const WhitepaperPanel: React.FC<WhitepaperPanelProps> = ({ productId, initialGated, productStatus, onGatedChange }) => {
  const [info, setInfo] = useState<WhitepaperInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [sourceArticleName, setSourceArticleName] = useState<string | null>(null);
  const [sourceArticleMissing, setSourceArticleMissing] = useState(false);

  const [gated, setGated] = useState(initialGated);
  const [gatedSaving, setGatedSaving] = useState(false);

  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');

  const [reExtracting, setReExtracting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [downloading, setDownloading] = useState(false);

  const loadInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getWhitepaper(productId);
      setInfo(data);
      if (data.source_task_id) {
        try {
          const task = await getTask(data.source_task_id);
          setSourceArticleName(task.name);
          setSourceArticleMissing(false);
        } catch {
          setSourceArticleMissing(true);
        }
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load whitepaper details.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    setGated(initialGated);
  }, [initialGated]);

  const handleGatedToggle = async () => {
    const next = !gated;
    setGatedSaving(true);
    try {
      await apiClient.patch(`/products/${productId}`, { whitepaper_gated: next });
      setGated(next);
      onGatedChange?.(next);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to update gating.'));
    } finally {
      setGatedSaving(false);
    }
  };

  const doRegenerate = async (forceChecklist: boolean) => {
    if (forceChecklist) {
      setReExtracting(true);
    } else {
      setRegenerating(true);
    }
    setRegenerateError('');
    try {
      await regenerateWhitepaper(productId, forceChecklist);
      await loadInfo();
    } catch (err: any) {
      setRegenerateError(getApiError(err, 'Failed to regenerate the PDF.'));
    } finally {
      setRegenerating(false);
      setReExtracting(false);
    }
  };

  const handleRegenerateClick = () => {
    if (!window.confirm('This replaces the current PDF. Continue?')) return;
    doRegenerate(false);
  };

  const handleReExtractClick = () => {
    doRegenerate(true);
  };

  const handlePreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const html = await previewWhitepaper(productId);
      setPreviewHtml(html);
    } catch (err: any) {
      setPreviewError(getApiError(err, 'Failed to load preview.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const blob = await downloadWhitepaperAsSeller(productId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = info?.file?.original_filename || 'whitepaper.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to download the whitepaper.'));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <div className="whitepaper-panel-loading">Loading whitepaper details…</div>;
  }

  if (error && !info) {
    return <div className="products-error">{error}</div>;
  }

  if (!info) return null;

  const status = info.generation_status || 'draft';
  const checklist = info.whitepaper_options?.checklist;
  const isRendering = status === 'rendering';

  return (
    <div className="whitepaper-panel">
      <div className="whitepaper-panel-header">
        <FileText size={18} />
        <h3>Whitepaper</h3>
        <span className={`whitepaper-status-pill ${status}`}>{STATUS_LABELS[status] || status}</span>
      </div>

      <div className="whitepaper-panel-meta">
        <div className="whitepaper-panel-meta-row">
          <span className="whitepaper-panel-meta-label">Source article</span>
          <span className="whitepaper-panel-meta-value">
            {sourceArticleMissing ? (
              <span className="whitepaper-panel-warning">
                <AlertTriangle size={13} /> Source article has been deleted
              </span>
            ) : info.source_task_id ? (
              <a href={`/preview/${info.source_task_id}`} target="_blank" rel="noopener noreferrer">
                {sourceArticleName || info.source_task_id} <ExternalLink size={12} />
              </a>
            ) : (
              '—'
            )}
          </span>
        </div>
        <div className="whitepaper-panel-meta-row">
          <span className="whitepaper-panel-meta-label">Pages</span>
          <span className="whitepaper-panel-meta-value">{info.file?.page_count ?? '—'}</span>
        </div>
        <div className="whitepaper-panel-meta-row">
          <span className="whitepaper-panel-meta-label">File size</span>
          <span className="whitepaper-panel-meta-value">{formatFileSize(info.file?.file_size)}</span>
        </div>
        <div className="whitepaper-panel-meta-row">
          <span className="whitepaper-panel-meta-label">Generated</span>
          <span className="whitepaper-panel-meta-value">
            {info.generated_at ? new Date(info.generated_at).toLocaleString() : '—'}
          </span>
        </div>
      </div>

      {status === 'failed' && info.generation_error && (
        <div className="modal-error">{info.generation_error}</div>
      )}
      {regenerateError && <div className="modal-error">{regenerateError}</div>}

      {/* IMPR-008: whitepapers are created status='draft', and BOTH delivery
          paths (deliver_lead_magnet's gated flow and the public ungated
          /shop/whitepaper/{id}/download route) hard-require status='active'.
          A freshly generated whitepaper therefore delivers nothing to
          anyone until the product is published, with no error anywhere to
          explain why -- surface that explicitly here. */}
      {productStatus !== 'active' && (
        <div className="whitepaper-panel-warning-banner">
          <AlertTriangle size={14} />
          <span>
            This product is still <strong>{productStatus}</strong>. Nobody can download this whitepaper —
            gated or ungated — until you set the product to <strong>Active</strong> in the product settings.
          </span>
        </div>
      )}

      <div className="whitepaper-panel-actions">
        <GlassButton
          variant="primary"
          icon={<RefreshCw size={16} />}
          onClick={handleRegenerateClick}
          loading={regenerating}
          disabled={isRendering || !info.source_task_id || sourceArticleMissing}
        >
          Regenerate PDF
        </GlassButton>
        {info.file && (
          <GlassButton
            variant="secondary"
            icon={<Download size={16} />}
            onClick={handleDownload}
            loading={downloading}
          >
            Download PDF
          </GlassButton>
        )}
        <GlassButton variant="secondary" onClick={handlePreview}>
          Preview
        </GlassButton>
        <GlassButton
          variant="secondary"
          icon={<Lock size={16} />}
          onClick={handleGatedToggle}
          loading={gatedSaving}
        >
          {gated ? 'Gated (require form)' : 'Ungated (public link)'}
        </GlassButton>
      </div>

      {/* Checklist */}
      {info.checklist_status && (
        <div className="whitepaper-checklist-block">
          <div className="whitepaper-panel-header" style={{ marginTop: '4px' }}>
            <h4 style={{ margin: 0 }}>Action checklist</h4>
            {info.checklist_status === 'ready' && <span className="whitepaper-status-pill ready">Ready</span>}
            {info.checklist_status === 'empty' && <span className="whitepaper-status-pill draft">Empty</span>}
            {info.checklist_status === 'failed' && <span className="whitepaper-status-pill failed">Failed</span>}
          </div>

          {info.checklist_status === 'ready' && checklist?.items && checklist.items.length > 0 && (
            <>
              {checklist.title && <p className="whitepaper-checklist-title">{checklist.title}</p>}
              <ul className="whitepaper-checklist-list">
                {checklist.items.map((item, i) => (
                  <li key={i} className="whitepaper-checklist-item">
                    <span className="whitepaper-checklist-item-text">{item.text}</span>
                    {item.detail && <span className="whitepaper-checklist-item-detail">{item.detail}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}

          {info.checklist_status === 'empty' && (
            <p className="whitepaper-hint">No actionable steps were found in this article.</p>
          )}

          {info.checklist_status === 'failed' && (
            <p className="whitepaper-hint">Checklist extraction failed. You can retry below.</p>
          )}

          <GlassButton
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={handleReExtractClick}
            loading={reExtracting}
            disabled={isRendering}
          >
            Re-extract checklist
          </GlassButton>
          <p className="whitepaper-hint">
            Editing individual checklist items in place isn't available yet — this needs a follow-up backend
            endpoint that partially patches <code>whitepaper_options</code>. Re-extract re-runs the AI pass fresh.
          </p>
        </div>
      )}

      {/* Preview modal */}
      {previewOpen && (
        <div className="modal-overlay" onClick={() => setPreviewOpen(false)}>
          <div className="modal-panel whitepaper-preview-panel" onClick={e => e.stopPropagation()}>
            <div className="whitepaper-panel-header">
              <h3>Preview</h3>
            </div>
            {previewLoading && <div className="whitepaper-hint">Rendering preview…</div>}
            {previewError && <div className="modal-error">{previewError}</div>}
            {previewHtml && (
              <iframe
                title="Whitepaper preview"
                srcDoc={previewHtml}
                className="whitepaper-preview-iframe"
              />
            )}
            <div className="modal-actions">
              <GlassButton variant="secondary" onClick={() => setPreviewOpen(false)}>
                Close
              </GlassButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhitepaperPanel;
